import { Check } from "lucide-react";
import Link from "@/components/ui/RouterLink";
import { PageHero } from "@/components/ui/PageHero";
import { SectionEyebrow } from "@/components/ui/SectionEyebrow";
import { RedirectNotice } from "@/components/ui/RedirectNotice";
import { TextField, TextAreaField, SelectField } from "@/components/ui/form/FormField";
import { FormAlert } from "@/components/ui/form/FormAlert";
import { pageSeo } from "@/config/seo";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { analytics } from "@/lib/analytics";
import { emailPlan } from "@/data/products";

const seo = pageSeo(
  "Business Email — Professional Mailboxes for Modern Teams",
  "Give your team a dependable business communication solution with a 30 GB mailbox and advanced email features."
);

const features = [
  "30 GB mailbox storage",
  "Advanced communication features",
  "Professional business email identity",
  "Multi-device access",
  "Organised inbox experience",
  "Business-focused support",
];

type EmailFormValues = {
  name: string;
  email: string;
  phone: string;
  company: string;
  mailboxes: string;
  provider: string;
  domain: string;
  migration: string;
  message: string;
};

function validate(values: EmailFormValues) {
  const errors: Partial<Record<keyof EmailFormValues, string>> = {};
  if (!values.name?.trim()) errors.name = "Please enter your name.";
  if (!values.email?.trim()) errors.email = "Please enter a business email.";
  else if (!/^\S+@\S+\.\S+$/.test(values.email)) errors.email = "Enter a valid email address.";
  if (!values.mailboxes?.trim()) errors.mailboxes = "Let us know how many mailboxes you need.";
  return errors;
}

export function EmailSolutionsPage() {
  useDocumentMeta(seo.title, seo.description);
  const { status, errors, handleSubmit } = useFormSubmit<EmailFormValues>(
    validate,
    "consultation_form_submit"
  );

  return (
    <>
      <PageHero
        eyebrow="Business Email"
        title="Professional Business Email With Room to Work"
        description="Give your team a dependable business communication solution with a 30 GB mailbox and advanced email features."
      />

      <section className="relative overflow-hidden border-b border-border bg-gradient-to-br from-navy-secondary to-navy py-20">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-5 lg:grid-cols-2 lg:px-8">
          <div>
            <SectionEyebrow>Professional Business Email</SectionEyebrow>
            <h2 className="mt-5 font-display text-2xl font-semibold text-text-primary sm:text-3xl">
              Your Business Deserves More Than a Basic Inbox
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-text-secondary">
              Communicate using a professional business email solution with a 30 GB mailbox and
              advanced features created for everyday business communication.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <RedirectNotice
                href={emailPlan.buyNowUrl}
                eventPayload={{ product: "email" }}
                className="focus-ring inline-flex items-center justify-center gap-2 rounded-full bg-gradient-brand px-6 py-3 text-sm font-medium text-white transition hover:brightness-110"
              >
                Buy Email Solution
              </RedirectNotice>
              <a
                href="#enquiry"
                onClick={() => analytics.track("email_plan_click", { intent: "migration" })}
                className="focus-ring inline-flex items-center justify-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium text-text-primary transition hover:border-cyan/50"
              >
                Ask About Email Migration
              </a>
            </div>
          </div>

          <div className="glass-panel rounded-card p-8">
            <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
              {emailPlan.name}
            </p>
            <p className="mt-2 font-display text-4xl font-semibold text-text-primary">
              Starting at {emailPlan.price}
            </p>
            <p className="mt-1 text-xs text-text-muted">
              Billing period to be confirmed in the final catalogue.
            </p>
            <ul className="mt-6 space-y-3 border-t border-border pt-6">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-text-secondary">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-status-success" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section id="enquiry" className="mx-auto max-w-3xl px-5 py-20 lg:px-8">
        <div className="text-center">
          <SectionEyebrow>Email Enquiry</SectionEyebrow>
          <h2 className="mt-5 font-display text-3xl font-semibold text-text-primary sm:text-4xl">
            Ask About Email Migration
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-sm text-text-secondary">
            Moving from another provider? Tell us a bit about your current setup and we'll guide
            you through the switch.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="glass-panel mt-10 space-y-5 rounded-card p-6 sm:p-10" noValidate>
          <input type="text" name="company_website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

          {status === "success" && <FormAlert status="success" />}
          {status === "error" && <FormAlert status="error" />}

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <TextField label="Name" name="name" required error={errors.name} />
            <TextField label="Business Email" name="email" type="email" required error={errors.email} />
            <TextField label="Phone" name="phone" type="tel" />
            <TextField label="Company" name="company" />
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <TextField label="Number of Mailboxes Required" name="mailboxes" required error={errors.mailboxes} />
            <TextField label="Existing Email Provider" name="provider" />
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <TextField label="Domain Name" name="domain" placeholder="e.g. yourcompany.com" />
            <SelectField label="Migration Assistance Required" name="migration" defaultValue="">
              <option value="" disabled>Select an option</option>
              <option value="yes">Yes, please assist with migration</option>
              <option value="no">No, we'll handle migration ourselves</option>
              <option value="not-sure">Not sure yet</option>
            </SelectField>
          </div>
          <TextAreaField label="Message" name="message" placeholder="Anything else we should know" />

          <button
            type="submit"
            disabled={status === "submitting"}
            className="focus-ring w-full rounded-full bg-gradient-brand px-6 py-3.5 text-sm font-medium text-white transition hover:brightness-110 disabled:opacity-60 sm:w-auto"
          >
            {status === "submitting" ? "Sending…" : "Send Enquiry"}
          </button>
        </form>
      </section>

      <section className="border-t border-border bg-navy-secondary/40 py-16 text-center">
        <p className="text-sm text-text-secondary">Prefer to talk it through first?</p>
        <Link href="/contact" className="focus-ring mt-3 inline-flex items-center gap-2 text-sm font-medium text-cyan hover:underline">
          Contact our team
        </Link>
      </section>
    </>
  );
}
